import React, { useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider, StyledEngineProvider } from '@mui/material/styles';

import { useSelector, Provider as ReduxProvider } from 'react-redux';
import { Router } from 'react-router';

import ConfirmationProvider from 'components/Confirmation/ConfirmationProvider';
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18n from 'providers/i18n';

import store from 'redux/store';
import history from 'providers/history';
import GlobalStyle from 'providers/globalStyle';
import ThemeColorTransition from 'providers/ThemeColorTransition';
import createTheme from 'providers/createTheme';
import { authSelectors } from 'redux/auth';
import SnackbarProvider from 'providers/SnackbarProvider';
import IntercomProvider from 'providers/intercom';
import ScrollToTop from 'components/ScrollToTop';

import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV2';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { QueryParamProvider } from 'use-query-params';
import { getLocale } from 'services/date-fns';
import { parse, stringify } from 'query-string';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';
import ZendeskProvider from 'providers/ZendeskProvider';
import ChatProvider from 'providers/ChatProvider';
import { LicenseInfo } from '@mui/x-license';
import config from 'config';
import LayoutProvider from './Layouts/LayoutProvider';


const queryParamProviderOptions = {
  searchStringToObject: parse,
  objectToSearchString: stringify,
};

// The lightest gray that still gets white contrast text, so button labels don't flip to black and back
const settlingColor = '#8f8f8f';

const ThemeProviders = ({ children }) => {

  const { i18n: { language } } = useTranslation();

  const isLoggedIn = useSelector(authSelectors.getUserIsLoggedIn);
  const workspaceColor = useSelector(authSelectors.getWorkspaceColor);

  // Neutral until auth says which color applies, so the page fades into it instead of flashing the brand green
  const color = isLoggedIn === null ? settlingColor : workspaceColor;
  const theme = useMemo(() => createTheme({ color, isDark: false, language }), [color, language]);

  LicenseInfo.setLicenseKey(config.mui.licenseKey);

  return (
    <StyledEngineProvider injectFirst>
      {/* Set themes for material-ui & @emotion */}
      <MuiThemeProvider theme={theme}>
        {/* Inject global styles */}
        <GlobalStyle />
        <ThemeColorTransition />
        {children}
      </MuiThemeProvider>
    </StyledEngineProvider>
  );
};

const DatePickerLocalizationProvider = args => {

  // Needed to trigger re-renders on language change
  const { i18n: { language } } = useTranslation();

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={getLocale(language)}
      {...args}
    />
  );
};

const Providers = ({ children }) => {

  return (
    <I18nextProvider i18n={i18n}>
      {/* Initiate redux store */}
      <ReduxProvider store={store}>

        {/* Configure all theme related stuff */}
        <ThemeProviders>

          <LayoutProvider>

            {/* Activate Router */}
            <Router history={history}>

              <QueryParamProvider
                adapter={ReactRouter5Adapter}
                options={queryParamProviderOptions}
              >

                <DatePickerLocalizationProvider>
                  <SnackbarProvider>

                    <ConfirmationProvider>
                      <IntercomProvider>
                        <ZendeskProvider>
                          <ChatProvider>
                            <ScrollToTop>
                              {children}
                            </ScrollToTop>
                          </ChatProvider>
                        </ZendeskProvider>
                      </IntercomProvider>
                    </ConfirmationProvider>

                  </SnackbarProvider>
                </DatePickerLocalizationProvider>

              </QueryParamProvider>

            </Router>

          </LayoutProvider>

        </ThemeProviders>
      </ReduxProvider>
    </I18nextProvider>
  );
};

export default Providers;
